import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, Chip, Avatar, Stack, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import { getColaboraciones, responderColaboracion } from '../../actions/colaboraciones';
import { getNotifications } from '../../actions/notification';
import { urlImg, textoDuracion } from '../../components/common/helpers';
import { nombreDe, fotoDe } from '../../components/common/imparte';
import { dinero } from '../../utils/dinero';

// "Servicios que imparto": lo que la persona imparte (o le pidieron impartir) en grupos y negocios de otras personas.
// Aquí responde sus invitaciones con todos los datos a la vista y puede dejar de impartir un servicio. Las invitaciones salen de
// las páginas, no de las notificaciones: siguen aquí aunque el aviso se haya perdido.

// Lo que se le pregunta antes de una acción que afecta al negocio de otra persona
const CONFIRMAR = {
  rechazar: {
    titulo: '¿Rechazar la invitación?',
    texto: (servicio, pagina) => `Le avisaremos a ${pagina} que no vas a impartir «${servicio}». Podrán invitarte de nuevo más adelante.`,
    boton: 'Rechazar',
  },
  dejar: {
    titulo: '¿Dejar de impartir este servicio?',
    texto: (servicio, pagina) => `«${servicio}» dejará de poder contratarse hasta que ${pagina} elija a otra persona. Las reservas que ya existen las atiende ${pagina}.`,
    boton: 'Dejar de impartirlo',
  },
};

const ServicioInvitado = ({ item, pagina, dueno, ocupado, onAceptar, onPedirConfirmacion, onVer }) => {
  const pendiente = item.estado === 'pendiente';
  const duracion = textoDuracion(item.duracionEstimada, item.unidadDuracion);
  const img = item.img ? `${urlImg}/w_400,h_250,c_fill/servicios/${item._id}/${item.img}` : '/search.png';

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{ p: 2, mb: 2, borderRadius: '12px', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, borderColor: pendiente ? 'warning.main' : 'sutil' }}
    >
      <Box sx={{ width: { xs: '100%', sm: 200 }, height: 140, borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
        <img src={img} alt={item.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ wordBreak: 'break-word' }}>
              {item.nombre || 'Servicio'}
            </Typography>
            <Chip
              size="small"
              label={pendiente ? 'Esperan tu respuesta' : item.visible ? 'Lo impartes tú · visible al público' : 'Lo impartes tú · aún no visible'}
              sx={{ mt: 0.5, bgcolor: pendiente ? 'warning.main' : item.visible ? 'success.main' : 'sutil', color: 'primary.main', fontWeight: 700 }}
            />
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="h6" color="primary" sx={{ lineHeight: 1.1 }}>{dinero(item.costoServicio)}</Typography>
            <Typography variant="caption" color="text.secondary">por {item.tipoCosto || 'servicio'}</Typography>
          </Box>
        </Box>

        {item.descripcion && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
            {item.descripcion}
          </Typography>
        )}

        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {duracion && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption">{duracion}</Typography>
            </Box>
          )}
          {item.modalidades?.length > 0 && (
            <Typography variant="caption">{item.modalidades.join(', ')}</Typography>
          )}
          {item.ubicacion && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <LocationOnIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" noWrap>{item.ubicacion}</Typography>
            </Box>
          )}
        </Stack>

        {!pendiente && !item.visible && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {pagina.publicada ? 'El servicio está pausado o en revisión por parte del negocio.' : 'La página todavía no está publicada.'}
          </Typography>
        )}

        <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
          {dueno?._id && (
            <Button size="small" onClick={() => onVer(`/servicio/${dueno._id}/${item._id}`)} sx={{ textTransform: 'none' }}>
              Ver servicio
            </Button>
          )}
          {pendiente ? (
            <>
              <Button size="small" variant="outlined" color="error" disabled={ocupado} onClick={() => onPedirConfirmacion('rechazar')} sx={{ textTransform: 'none' }}>
                Rechazar
              </Button>
              <Button size="small" variant="contained" disabled={ocupado} onClick={onAceptar} sx={{ textTransform: 'none' }}>
                Aceptar
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined" color="error" disabled={ocupado} onClick={() => onPedirConfirmacion('dejar')} sx={{ textTransform: 'none' }}>
              Dejar de impartirlo
            </Button>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const Colaboraciones = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cargando, setCargando] = useState(true);
  const [lista, setLista] = useState([]);
  const [fallo, setFallo] = useState(false);
  const [aviso, setAviso] = useState(null);       // { tipo, texto }
  const [ocupado, setOcupado] = useState('');     // `${pagina}|${servicio}` que se está respondiendo
  const [confirmar, setConfirmar] = useState(null); // { accion, pagina, servicio }

  const cargar = useCallback(async () => {
    const res = await getColaboraciones();
    setLista(res.colaboraciones);
    setFallo(!res.ok);
    setCargando(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const responder = async (pagina, servicio, accion) => {
    const clave = `${pagina._id}|${servicio ? servicio._id : ''}`;
    setOcupado(clave);
    const res = await responderColaboracion(pagina._id, servicio ? servicio._id : null, accion);
    setOcupado('');
    setConfirmar(null);
    if (res.ok) {
      const que = servicio ? `«${servicio.nombre}»` : `la página «${pagina.nombre}»`;
      setAviso({
        tipo: 'success',
        texto: accion === 'aceptar' ? (servicio ? `Aceptaste impartir ${que}. Ya apareces como quien lo imparte.` : `Ya eres parte de ${que}.`)
          : accion === 'rechazar' ? `Rechazaste la invitación de ${que}. Ya se le avisó a quien te invitó.`
            : `Dejaste de impartir ${que}. Ya se le avisó al negocio.`,
      });
    } else {
      setAviso({ tipo: 'error', texto: res.error });
    }
    await cargar();
    dispatch(getNotifications()); // el aviso de esa invitación ya quedó respondido
  };

  const pendientes = lista.reduce((n, c) => n + c.servicios.filter(s => s.estado === 'pendiente').length + (c.invitacionAlEquipo ? 1 : 0), 0);

  return (
    <Box className="main-page">
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3 }}>
        <Typography variant="h5">Servicios que imparto</Typography>
        <Typography mt={2} variant="body1" color="primary">
          <small>Invitaciones y servicios que das en grupos y negocios de otras personas</small>
        </Typography>
      </Box>

      {aviso && (
        <Alert severity={aviso.tipo} onClose={() => setAviso(null)} sx={{ mb: 2, borderRadius: '12px' }}>
          {aviso.texto}
        </Alert>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : fallo ? (
        <Alert severity="error" sx={{ borderRadius: '12px' }} action={<Button color="inherit" size="small" onClick={cargar} sx={{ textTransform: 'none' }}>Reintentar</Button>}>
          No se pudieron cargar tus invitaciones.
        </Alert>
      ) : lista.length === 0 ? (
        <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px', textAlign: 'center' }}>
          <StorefrontOutlinedIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
          <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mt: 1 }}>
            Todavía no impartes servicios en otros negocios
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
            Cuando alguien te invite a impartir un servicio de su grupo o negocio, la invitación aparecerá aquí con los datos del
            servicio para que decidas si la aceptas.
          </Typography>
        </Paper>
      ) : (
        <>
          {pendientes > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
              Tienes {pendientes === 1 ? '1 invitación' : `${pendientes} invitaciones`} sin responder. Mientras no respondas, {pendientes === 1 ? 'ese servicio no se puede' : 'esos servicios no se pueden'} contratar.
            </Alert>
          )}

          {lista.map(({ pagina, dueno, invitacionAlEquipo, servicios }) => (
            <Box key={pagina._id} sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
                <Avatar src={fotoDe(dueno, 'w_80,h_80,c_fill') || undefined} sx={{ width: 40, height: 40 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ lineHeight: 1.2 }}>
                    {pagina.nombre || 'Página'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {nombreDe(dueno) ? `Te invita ${nombreDe(dueno)}` : 'Grupo o negocio'}{pagina.publicada ? '' : ' · página aún sin publicar'}
                  </Typography>
                </Box>
                <Button size="small" onClick={() => navigate(`/pagina/${pagina._id}`)} sx={{ textTransform: 'none', ml: 'auto' }}>
                  Ver página
                </Button>
              </Box>

              {invitacionAlEquipo && (
                <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '12px', borderColor: 'warning.main', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2" color="primary" sx={{ flex: 1, minWidth: 200 }}>
                    Te invitaron a formar parte del equipo de esta página.
                  </Typography>
                  <Button size="small" variant="outlined" color="error" disabled={ocupado === `${pagina._id}|`} onClick={() => setConfirmar({ accion: 'rechazar', pagina, servicio: null })} sx={{ textTransform: 'none' }}>
                    Rechazar
                  </Button>
                  <Button size="small" variant="contained" disabled={ocupado === `${pagina._id}|`} onClick={() => responder(pagina, null, 'aceptar')} sx={{ textTransform: 'none' }}>
                    Aceptar
                  </Button>
                </Paper>
              )}

              {servicios.map(item => (
                <ServicioInvitado
                  key={item._id}
                  item={item}
                  pagina={pagina}
                  dueno={dueno}
                  ocupado={ocupado === `${pagina._id}|${item._id}`}
                  onAceptar={() => responder(pagina, item, 'aceptar')}
                  onPedirConfirmacion={(accion) => setConfirmar({ accion, pagina, servicio: item })}
                  onVer={navigate}
                />
              ))}
            </Box>
          ))}
        </>
      )}

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)} aria-labelledby="confirmar-colaboracion">
        {confirmar && (
          <>
            <DialogTitle id="confirmar-colaboracion">{CONFIRMAR[confirmar.accion].titulo}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: 'text.primary' }}>
                {confirmar.servicio
                  ? CONFIRMAR[confirmar.accion].texto(confirmar.servicio.nombre, `«${confirmar.pagina.nombre}»`)
                  : `Le avisaremos a «${confirmar.pagina.nombre}» que no formarás parte de su equipo.`}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ pb: 2, px: 3 }}>
              <Button onClick={() => setConfirmar(null)} sx={{ textTransform: 'none' }}>Cancelar</Button>
              <Button
                variant="contained"
                color="error"
                disabled={Boolean(ocupado)}
                onClick={() => responder(confirmar.pagina, confirmar.servicio, confirmar.accion)}
                sx={{ textTransform: 'none' }}
              >
                {CONFIRMAR[confirmar.accion].boton}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Colaboraciones;
